import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import { Modal, Button } from "react-bootstrap";
import Notification from "../../component/notification";
import AddCourseForm from "./AddCourseForm";
import { useNavigate, useLocation } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  writeBatch,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

const Course = () => {
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [notification, setNotification] = useState({
    visible: false,
    message: "",
    color: "",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const coursesPerPage = 10;

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "courses"));
        const coursesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCourses(coursesData);
      } catch (error) {
        console.error("Error fetching courses: ", error);
      }
    };
    fetchCourses();
  }, []);

  const filtered = courses.filter((course) =>
    course.title.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / coursesPerPage);
  const paginated = filtered.slice(
    (page - 1) * coursesPerPage,
    page * coursesPerPage
  );
  

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleShowDeleteModal = (index) => {
    setCourseToDelete(index);
    setShowDeleteModal(true);
  };

  // Function to delete course selection from additional collection
  const deleteCourseSelection = async (courseId) => {
    try {
      await deleteDoc(doc(db, "courseselection", courseId));
      //console.log('Course selection deleted successfully from additional collection');
    } catch (error) {
      console.error('Error deleting from courseselection collection:', error);
      // Don't show error to user as this is additional functionality
    }
  };

  const handleDeleteCourse = async () => {
    try {
      const courseId = courses[courseToDelete].id;
      await deleteDoc(doc(db, "courses", courseId));
      setCourses((prev) => prev.filter((_, i) => i !== courseToDelete));
      setNotification({
        visible: true,
        message: "❌ Course deleted successfully!",
        color: "danger",
      });
      setTimeout(
        () => setNotification({ visible: false, message: "", color: "" }),
        3000
      );
      setShowDeleteModal(false);

      // Delete from courseselection collection (additional operation)
      try {
        await deleteCourseSelection(courseId);
      } catch (error) {
        console.error('Error deleting from courseselection collection:', error);
        // Don't show error to user as this is additional functionality
      }

    } catch (error) {
      console.error("Error deleting course: ", error);
      setNotification({
        visible: true,
        message: "❌ Failed to delete course!",
        color: "danger",
      });
      setTimeout(
        () => setNotification({ visible: false, message: "", color: "" }),
        3000
      );
    }
  };

  // Function to save/update course selection in additional collection
  const saveCourseSelection = async (courseId, courseTitle) => {
    try {
      const courseSelectionRef = doc(db, 'courseselection', courseId);
      
      await setDoc(courseSelectionRef, {
        title: courseTitle,
        courseId: courseId,
        createdAt: new Date(),
        updatedAt: new Date()
      }, { merge: true });

      //console.log('Course selection saved successfully to additional collection');
    } catch (error) {
      console.error('Error saving to courseselection collection:', error);
      // Don't show error to user as this is additional functionality
    }
  };

  // Smart day document synchronization - conserve existing, only add/delete as per duration
  const smartSynchronizeContentDays = async (courseId, newDuration, isNewCourse = false) => {
    try {
      const dayRef = collection(db, "courses", courseId, "day");
      const snapshot = await getDocs(dayRef);
      const existingDayIds = snapshot.docs.map((doc) => doc.id);
      
      // Get existing day numbers (only valid day documents)
      const existingDayNumbers = existingDayIds
        .filter(id => /^day \d+$/.test(id))
        .map(id => parseInt(id.split(" ")[1]))
        .sort((a, b) => a - b);

      const batch = writeBatch(db);
      let hasChanges = false;
      
      if (isNewCourse) {
        // For new courses, create all day documents from 1 to duration
        for (let i = 1; i <= newDuration; i++) {
          const dayId = `day ${i}`;
          const defaultDaySettings = {
            dayTitle: `Day ${i}`,
            autoApproval: true,
            manualApproval: false,
            assignmentRequired: false,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          batch.set(doc(db, "courses", courseId, "day", dayId), defaultDaySettings);
          hasChanges = true;
        }
        //console.log(`Creating ${newDuration} day documents for new course`);
      } else {
        // For existing courses, conserve existing docs and only add/delete as needed
        
        // STEP 1: Add missing day documents (1 to newDuration)
        const missingDays = [];
        for (let i = 1; i <= newDuration; i++) {
          if (!existingDayNumbers.includes(i)) {
            missingDays.push(i);
            const dayId = `day ${i}`;
            const defaultDaySettings = {
              dayTitle: `Day ${i}`,
              autoApproval: true,
              manualApproval: false,
              assignmentRequired: false,
              createdAt: new Date(),
              updatedAt: new Date()
            };
            batch.set(doc(db, "courses", courseId, "day", dayId), defaultDaySettings);
            hasChanges = true;
          }
        }
        
        // STEP 2: Delete excess day documents (beyond newDuration)
        const excessDays = existingDayNumbers.filter(dayNum => dayNum > newDuration);
        for (const dayNum of excessDays) {
          const dayId = `day ${dayNum}`;
          batch.delete(doc(db, "courses", courseId, "day", dayId));
          hasChanges = true;
        }
        
        // Log changes
        if (missingDays.length > 0) {
          //console.log(`Adding missing day documents: ${missingDays.join(', ')}`);
        }
        if (excessDays.length > 0) {
          //console.log(`Removing excess day documents: ${excessDays.join(', ')}`);
        }
        if (!hasChanges) {
          //console.log(`No day document changes needed. Existing: [${existingDayNumbers.join(', ')}], Required: 1-${newDuration}`);
        }
      }
      
      // Only commit if there are changes
      if (hasChanges) {
        await batch.commit();
        //console.log(`Day documents synchronized for course ${courseId}`);
      }
    } catch (error) {
      console.error("Error synchronizing day documents:", error);
      throw error;
    }
  };

  const handleAddCourse = async (course) => {
    try {
      let message = "";
      
      if (editIndex !== null) {
        // EDIT MODE - Update existing document
        const existingCourse = courses[editIndex];
        const courseId = existingCourse.id;
        const oldDuration = existingCourse.duration;
        const newDuration = course.duration;
        
        // Update the document in Firestore (excluding the id field)
        const { id, ...courseDataToUpdate } = course;
        await updateDoc(doc(db, "courses", courseId), courseDataToUpdate);
        
        // Update local state
        const updatedCourses = [...courses];
        updatedCourses[editIndex] = { ...course, id: courseId }; // Keep original ID
        setCourses(updatedCourses);
        
        message = "✔️Course updated successfully!";
        
        // Only synchronize content days if duration changed
        if (oldDuration !== newDuration) {
          await smartSynchronizeContentDays(courseId, newDuration, false);
          //console.log(`Duration changed from ${oldDuration} to ${newDuration} days`);
        } else {
          //console.log("Duration unchanged, skipping day document synchronization");
        }

        // Update course selection in additional collection
        try {
          await saveCourseSelection(courseId, course.title);
        } catch (error) {
          console.error('Error updating courseselection collection:', error);
        }

      } else {
        // ADD MODE - Create new document (handled by AddCourseForm)
        const newCourses = [course, ...courses];
        setCourses(newCourses);
        message = "✔️Course added successfully!";
        
        // For new courses, create all day documents
        await smartSynchronizeContentDays(course.id, course.duration, true);

        // Save to courseselection collection (additional operation)
        try {
          await saveCourseSelection(course.id, course.title);
        } catch (error) {
          console.error('Error saving to courseselection collection:', error);
        }
      }
      
      setShowForm(false);
      setEditIndex(null);
      setPage(1);
      setNotification({ visible: true, message, color: "success" });
      setTimeout(
        () => setNotification({ visible: false, message: "", color: "" }),
        3000
      );
    } catch (error) {
      console.error("Error handling course: ", error);
      setNotification({
        visible: true,
        message: "❌ Failed to save course!",
        color: "danger",
      });
      setTimeout(
        () => setNotification({ visible: false, message: "", color: "" }),
        3000
      );
    }
  };

  const navigate = useNavigate();
  const location = useLocation();
  
  React.useEffect(() => {
    if (location.state?.newCourse) {
      setCourses((prev) => [location.state.newCourse, ...prev]);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  return (
    <div className="container-fluid py-4">
      <Notification
        message={notification.message}
        color={notification.color}
        visible={notification.visible}
      />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="fw-bold mb-1">Courses</h2>
          <div className="text-muted">
            Create and manage courses for internships
          </div>
        </div>
        <button
          className="btn btn-dark d-flex align-items-center gap-2"
          onClick={() => navigate("/courses/add")}
        >
          <i className="bi bi-plus-circle"></i> Add Course
        </button>
      </div>

      <div className="bg-white rounded-3 p-4 shadow-sm border">
        <h4 className="fw-bold mb-1">Course Management</h4>
        <div className="text-muted mb-3">
          View and manage all courses in the system
        </div>

        <input
          type="text"
          className="form-control mb-3"
          placeholder="Search courses..."
          value={search}
          onChange={handleSearch}
          style={{ maxWidth: 350 }}
        />

        <div className="table-responsive" style={{ minWidth: "1200px" }}>
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: "29%" }}>Title</th>
                <th style={{ width: "25%" }}>Category</th>
                <th style={{ width: "15%" }}>Duration (Days)</th>
                <th style={{ width: "10%" }}></th>
                <th style={{ width: "10%" }}></th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted">
                    No courses found.
                  </td>
                </tr>
              ) : (
                paginated.map((course, idx) => (
                  <tr key={course.id}>
                    <td>{course.title}</td>
                    <td>{course.category}</td>
                    <td>{course.duration ? `${course.duration} days` : "-"}</td>
                    <td>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() =>
                          navigate("/coursecontent", {
                            state: {
                              course:
                                courses[(page - 1) * coursesPerPage + idx],
                            },
                          })
                        }
                      >
                        Manage Content
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-link p-1"
                        title="Edit"
                        onClick={() =>
                          navigate(
                            `/courses/edit/${
                              (page - 1) * coursesPerPage + idx
                            }`,
                            {
                              state: {
                                course:
                                  courses[(page - 1) * coursesPerPage + idx],
                              },
                            }
                          )
                        }
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn btn-link p-1 text-danger"
                        title="Delete"
                        onClick={() =>
                          handleShowDeleteModal(
                            (page - 1) * coursesPerPage + idx
                          )
                        }
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted small">
            Showing{" "}
            {filtered.length === 0 ? 0 : (page - 1) * coursesPerPage + 1} to{" "}
            {Math.min(page * coursesPerPage, filtered.length)} of{" "}
            {filtered.length} entries
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-light btn-sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              &lt;
            </button>
            <span className="small">
              Page {page} of {totalPages || 1}
            </span>
            <button
              className="btn btn-light btn-sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages || totalPages === 0}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>

      {showForm && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: "#0002" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editIndex !== null ? "Edit Course" : "Add Course"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowForm(false);
                    setEditIndex(null);
                  }}
                ></button>
              </div>
              <AddCourseForm
                onSubmit={handleAddCourse}
                onCancel={() => {
                  setShowForm(false);
                  setEditIndex(null);
                }}
                initialValues={
                  editIndex !== null ? courses[editIndex] : undefined
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this course?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteCourse}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Course;